import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Download, Database, Wifi, WifiOff, RefreshCw, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '../../ui/alert';
import { toast } from 'sonner';
import { format, subDays } from 'date-fns';
import { DateRangeFilter } from '../../DateRangeFilter';
import { API_BASE } from '../../../utils/api-base';

interface OfflineDataViewerScreenProps {
  clientId: string;
  token: string;
}

type OfflineReadingType = 'plc' | 'energy_meter';

interface OfflineBridgeReading {
  id: string;
  data_type: OfflineReadingType;
  device_id: string;
  device_name?: string | null;
  tag_id: string;
  tag_name?: string | null;
  value: number | string;
  unit?: string | null;
  reading_timestamp: string;
  offline_queued_at?: string | null;
  synced_at?: string | null;
  source?: string | null;
}

interface OfflineBridgeStats {
  totalReadings: number;
  plcReadings: number;
  meterReadings: number;
}

export function OfflineDataViewerScreen({ clientId, token }: OfflineDataViewerScreenProps) {
  const [readings, setReadings] = useState<OfflineBridgeReading[]>([]);
  const [stats, setStats] = useState<OfflineBridgeStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [formatType, setFormatType] = useState<'csv' | 'json'>('csv');
  const [dataTypeFilter, setDataTypeFilter] = useState<'all' | OfflineReadingType>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [startDate, setStartDate] = useState(subDays(new Date(), 7));
  const [endDate, setEndDate] = useState(new Date());
  const ITEMS_PER_PAGE = 50;

  const loadData = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        limit: '10000',
        offset: '0',
      });

      if (dataTypeFilter !== 'all') {
        params.set('type', dataTypeFilter);
      }

      const response = await fetch(
        `${API_BASE}/api/clients/${clientId}/offline-bridge-data?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setReadings(data.readings || []);
      }

      const statsResponse = await fetch(
        `${API_BASE}/api/clients/${clientId}/offline-bridge-data/stats`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData);
      }
    } catch (error) {
      console.error('Failed to load offline data:', error);
      toast.error('Failed to load offline data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [clientId, startDate, endDate, dataTypeFilter]);

  const filteredReadings = [...readings]
    .sort((a, b) => new Date(b.reading_timestamp).getTime() - new Date(a.reading_timestamp).getTime())
    .filter((r) => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        r.data_type?.toLowerCase().includes(term) ||
        r.tag_name?.toLowerCase().includes(term) ||
        r.device_name?.toLowerCase().includes(term) ||
        r.device_id?.toLowerCase().includes(term) ||
        r.value?.toString().toLowerCase().includes(term) ||
        r.unit?.toLowerCase().includes(term)
      );
    }
    return true;
  });

  const paginatedReadings = filteredReadings.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);
  const totalPages = Math.ceil(filteredReadings.length / ITEMS_PER_PAGE);

  const handleExport = async () => {
    if (filteredReadings.length === 0) {
      toast.error('No data to export');
      return;
    }

    try {
      let content: string;
      let filename: string;

      if (formatType === 'csv') {
        const csv = [
          'Timestamp,Type,Device,Tag,Value,Unit,OfflineQueuedAt,SyncedAt',
          ...filteredReadings.map((r) => `"${r.reading_timestamp}","${r.data_type}","${r.device_name || r.device_id || ''}","${r.tag_name || r.tag_id || ''}",${r.value},"${r.unit || ''}","${r.offline_queued_at || ''}","${r.synced_at || ''}"`),
        ].join('\n');
        content = csv;
        filename = `offline-data-${clientId}-${new Date().toISOString().split('T')[0]}.csv`;
      } else {
        const json = {
          clientId,
          exportDate: new Date().toISOString(),
          totalRecords: filteredReadings.length,
          data: filteredReadings,
        };
        content = JSON.stringify(json, null, 2);
        filename = `offline-data-${clientId}-${new Date().toISOString().split('T')[0]}.json`;
      }

      const blob = new Blob([content], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success(`Exported ${filteredReadings.length} readings as ${formatType.toUpperCase()}`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export data');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Offline Data Recovery</h1>
        <p className="text-muted-foreground mt-2">
          View only API-downtime readings recovered from bridge offline queue (PLC + Meter)
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Online Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              {navigator.onLine ? (
                <>
                  <Wifi className="h-5 w-5 text-green-500" />
                  <span className="text-lg font-bold text-green-600">Online</span>
                </>
              ) : (
                <>
                  <WifiOff className="h-5 w-5 text-red-500" />
                  <span className="text-lg font-bold text-red-600">Offline</span>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Readings</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{stats?.totalReadings || 0}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">PLC Offline</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">
                {stats?.plcReadings || 0}
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Meter Offline</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">
                {stats?.meterReadings || 0}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      <DateRangeFilter
        onDateRangeChange={(start, end) => {
          setStartDate(start);
          setEndDate(end);
          setPage(1);
        }}
        defaultPreset="week"
      />

      <Alert className="border-blue-200 bg-blue-50 dark:border-blue-900/50 dark:bg-blue-900/20">
        <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        <AlertDescription className="text-blue-800 dark:text-blue-200">
          <strong>Offline-only view:</strong> This screen lists only readings that were queued while API was down and later recovered to server DB.
        </AlertDescription>
      </Alert>

      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={loadData}
            className="gap-2 flex-1 sm:flex-none"
            variant="outline"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>

          <Button
            onClick={handleExport}
            disabled={filteredReadings.length === 0}
            variant="outline"
            className="gap-2 flex-1 sm:flex-none"
          >
            <Download className="h-4 w-4" />
            Export as {formatType.toUpperCase()}
          </Button>

          <Button
            onClick={() => setFormatType(formatType === 'csv' ? 'json' : 'csv')}
            variant="outline"
            className="flex-1 sm:flex-none"
          >
            Format: {formatType.toUpperCase()}
          </Button>

          <div className="flex gap-2 flex-1 sm:flex-none">
            <Button
              size="sm"
              variant={dataTypeFilter === 'all' ? 'default' : 'outline'}
              onClick={() => {
                setDataTypeFilter('all');
                setPage(1);
              }}
            >
              All
            </Button>
            <Button
              size="sm"
              variant={dataTypeFilter === 'plc' ? 'default' : 'outline'}
              onClick={() => {
                setDataTypeFilter('plc');
                setPage(1);
              }}
            >
              PLC
            </Button>
            <Button
              size="sm"
              variant={dataTypeFilter === 'energy_meter' ? 'default' : 'outline'}
              onClick={() => {
                setDataTypeFilter('energy_meter');
                setPage(1);
              }}
            >
              Meter
            </Button>
          </div>
        </div>

        <div>
          <Input
            placeholder="Search by type, device, tag, unit, or value..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            className="w-full"
          />
        </div>
      </div>

      {loading ? (
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-muted-foreground">Loading offline data...</p>
          </CardContent>
        </Card>
      ) : paginatedReadings.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <Database className="h-12 w-12 text-muted-foreground mx-auto mb-2 opacity-50" />
            <p className="text-muted-foreground">No recovered offline data in this period</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Collected Readings ({filteredReadings.length})</CardTitle>
            <CardDescription>Showing {paginatedReadings.length} of {filteredReadings.length} readings</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Device</TableHead>
                    <TableHead>Tag</TableHead>
                    <TableHead>Value</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead>Recovered</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedReadings.map((reading) => (
                    <TableRow key={reading.id}>
                      <TableCell className="text-xs">
                        {format(new Date(reading.reading_timestamp), 'MMM dd, HH:mm:ss')}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">
                          {reading.data_type === 'energy_meter' ? 'Meter' : 'PLC'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {reading.device_name || reading.device_id || '-'}
                      </TableCell>
                      <TableCell>{reading.tag_name || reading.tag_id || 'N/A'}</TableCell>
                      <TableCell className="font-mono font-bold">{reading.value}</TableCell>
                      <TableCell>{reading.unit || '-'}</TableCell>
                      <TableCell>
                        <Badge variant="default">
                          Synced
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-4">
                <p className="text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    variant="outline"
                  >
                    Previous
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    variant="outline"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
